import type { ContractAutofillPlan } from "@aam/shared";
import { getSessionToken } from "../context/SessionContext";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

export interface PortalAutofillPlan extends ContractAutofillPlan {
  summary: string;
  sensitivePreviews: Array<{ fieldId: string; masked: string; sourceLabel: string }>;
}

export interface RevealedAutofillField {
  fieldId: string;
  value: string;
  sourceLabel: string;
  submissionId: string;
}

function authHeaders(includeJson = false): HeadersInit {
  const token = getSessionToken();
  if (!token) throw new Error("Authentication required");
  return {
    Authorization: `Bearer ${token}`,
    ...(includeJson && { "Content-Type": "application/json" }),
  };
}

/**
 * GET /api/agents/me/contract-autofill — what this agent's next contract can
 * inherit. Sensitive values arrive masked; use {@link revealAutofillField} to
 * pull one forward.
 */
export async function loadContractAutofill(): Promise<PortalAutofillPlan | null> {
  try {
    const response = await fetch(`${API_BASE}/api/agents/me/contract-autofill`, {
      headers: authHeaders(),
    });
    const json = await response.json();
    if (!response.ok || !json.success) return null;
    return json.data as PortalAutofillPlan;
  } catch {
    // Autofill is an accelerator, never a gate — a failure just means the
    // agent fills the form out by hand.
    return null;
  }
}

function isBlankValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Suggested values for the answers the agent has not given yet.
 *
 * Autofill only ever writes into blanks: a resumed draft, a value the agent
 * typed over, and an answer they deliberately cleared all stay exactly as they
 * left them.
 */
export function autofillUpdatesForBlanks(
  formData: Record<string, unknown>,
  plan: PortalAutofillPlan | null
): Record<string, unknown> {
  if (!plan) return {};
  const updates: Record<string, unknown> = {};
  for (const [fieldId, value] of Object.entries(plan.values)) {
    if (isBlankValue(formData[fieldId]) && !isBlankValue(value)) {
      updates[fieldId] = value;
    }
  }
  return updates;
}

/** POST /api/agents/me/contract-autofill/reveal — one audited PII value. */
export async function revealAutofillField(fieldId: string): Promise<RevealedAutofillField> {
  const response = await fetch(`${API_BASE}/api/agents/me/contract-autofill/reveal`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify({ fieldId }),
  });
  const json = await response.json().catch(() => ({ success: false }));
  if (!response.ok || !json.success) {
    throw new Error(json.error || "Could not load that value");
  }
  return json.data as RevealedAutofillField;
}
